import { useEffect, useRef, useState } from "react";

/**
 * Hide the nav after scrolling down past a threshold; show again when scrolling up.
 * Uses rAF + passive scroll so scroll stays smooth.
 */
export function useNavScrollHidden({ threshold = 72, target = window } = {}) {
  const [hidden, setHidden] = useState(false);
  const lastYRef = useRef(0);

  useEffect(() => {
    if (!target?.addEventListener) return;

    let frameId = null;

    const readScrollY = () =>
      target.pageYOffset !== undefined ? target.pageYOffset : target.scrollTop;

    const evaluate = () => {
      frameId = null;
      const y = readScrollY();
      const scrollingUp = y < lastYRef.current;
      lastYRef.current = y;

      const nextHidden = scrollingUp ? false : y > threshold;
      setHidden((prev) => (prev === nextHidden ? prev : nextHidden));
    };

    const onScroll = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(evaluate);
    };

    lastYRef.current = readScrollY();
    evaluate();

    target.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      target.removeEventListener("scroll", onScroll);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [threshold, target]);

  return hidden;
}
